import { Controller } from '@hotwired/stimulus';
import { postGeneratorOpenAI, getPaywallController } from '../lib/generator_api';
import {
  applyDisplayFormula,
  bindFormulaDelimiterListener,
  delimiterPromptInstruction,
  processGeneratedFormula,
  unbindFormulaDelimiterListener,
} from '../lib/generator_formula_delimiter';

export default class extends Controller {
  static targets = [
    'usecase',
    'cell',
    'submit',
    'submitLabel',
    'placeholder',
    'loading',
    'output',
    'formulaText',
    'error',
    'successNote',
    'copyButton'
  ];

  static values = {
    tool: { type: String, default: 'formatter' }
  };

  connect() {
    bindFormulaDelimiterListener(this);
    this.formulaValue = '';
  }

  disconnect() {
    unbindFormulaDelimiterListener(this);
  }

  paywall() {
    return getPaywallController(this.element);
  }

  parseExample(event) {
    const raw = event.currentTarget.dataset.example;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      console.error('Invalid example payload');
      return null;
    }
  }

  loadExample(event) {
    const data = this.parseExample(event);
    if (!data) return;

    this.usecaseTarget.value = data.usecase;
    this.cellTarget.value = data.range;
    this.clearError();
    this.hideResult();
    this.highlightExampleCard(event.currentTarget);
    this.scrollToGenerator();
    this.usecaseTarget.focus();
  }

  async applyFormula(event) {
    const data = this.parseExample(event);
    if (!data?.formula) return;

    const { canonical, display } = processGeneratedFormula(data.formula);
    this.canonicalFormula = canonical;
    this.formulaValue = display;
    await this.copyToClipboard(display);
    this.showResult(display);
    this.highlightExampleCard(event.currentTarget);
    this.scrollToGenerator();
  }

  highlightExampleCard(button) {
    this.element.querySelectorAll('[data-example-card]').forEach((card) => {
      card.classList.remove('ring-2', 'ring-emerald-500/60', 'ring-offset-2');
    });
    button?.closest('[data-example-card]')?.classList.add('ring-2', 'ring-emerald-500/60', 'ring-offset-2');
  }

  scrollToGenerator() {
    document.getElementById('formatter-generator')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden');
      this.errorTarget.textContent = '';
    }
  }

  async submit(event) {
    event.preventDefault();
    this.clearError();

    const usecase = this.usecaseTarget.value.trim();
    if (!usecase) {
      this.showError('Describe the rule you want before generating.');
      return;
    }

    const cellRaw = this.cellTarget.value;
    const cellValue = !cellRaw || cellRaw.trim() === '' ? 'A1' : cellRaw.trim();

    const userRole =
      'You are a helpful conditional formatting generator. You will help generate a Google Sheets conditional formatting to use in a google sheet. ';
    const prompt =
      'You will generate the conditional formatting to use in a google sheet. Use any existing Google Sheets conditional formatting. Do not include the explanation in your response. Reply with only the conditional formatting including the equals (=) sign that starts every formula if using a custom formula in Conditional formatting. ' +
      String.raw`Use Case: `;

    this.setLoading(true);
    this.hideResult();

    try {
      const result = await postGeneratorOpenAI({
        userRole,
        prompt: prompt + usecase + delimiterPromptInstruction() + `\n        Range to use: ` + cellValue,
        generator: this.toolValue
      });

      if (result.limited) {
        this.paywall()?.handleLimitReached();
        return;
      }

      if (result.error) {
        console.error('Conditional formatting generator proxy error', result.response?.status, result.detail);
        this.showError('Could not generate a rule. Try again in a moment.');
        return;
      }

      const data = result.data;
      const rawFormatting = data.choices?.[0]?.message?.content?.trim();

      if (!rawFormatting) {
        console.error('OpenAI proxy returned no formatting', data);
        this.showError('No rule came back. Add more detail to your description and try again.');
        return;
      }

      const { canonical, display } = processGeneratedFormula(rawFormatting);
      this.canonicalFormula = canonical;
      this.formulaValue = display;
      await this.copyToClipboard(display);
      this.paywall()?.applyUsageFromResponse(data.generator_usage);
      this.showResult(display, data.generator_usage);

      if (data.generator_usage?.remaining === 1) {
        this.showSuccessNudge();
      }

      if (data.generator_usage?.remaining === 0) {
        this.paywall()?.handleLimitReached();
      }
    } catch (err) {
      console.error('Formatter request failed', err);
      this.showError('Connection failed. Check your network and try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async copy(event) {
    event?.preventDefault();
    if (!this.formulaValue) return;

    await this.copyToClipboard(this.formulaValue);
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Copied again.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard may be blocked; formula remains visible in the output panel.
    }
  }

  setLoading(isLoading) {
    if (this.hasSubmitTarget) {
      const paywall = this.paywall();
      const atLimit = paywall && !paywall.paidMemberValue && paywall.remainingValue === 0;
      if (atLimit) {
        paywall.syncUi(0);
      } else {
        this.submitTarget.disabled = isLoading;
      }
    }
    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.textContent = isLoading ? 'Generating…' : 'Generate';
    }
    if (this.hasLoadingTarget) this.loadingTarget.classList.toggle('hidden', !isLoading);
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.toggle('hidden', isLoading);
    if (isLoading && this.hasOutputTarget) this.outputTarget.classList.add('hidden');
    if (isLoading && this.hasCopyButtonTarget) this.copyButtonTarget.classList.add('hidden');
    if (isLoading && this.hasSuccessNoteTarget) this.successNoteTarget.classList.add('hidden');
  }

  hideResult() {
    if (this.hasOutputTarget) this.outputTarget.classList.add('hidden');
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove('hidden');
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.add('hidden');
    if (this.hasSuccessNoteTarget) this.successNoteTarget.classList.add('hidden');
  }

  showResult(formula, usage) {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden');
    applyDisplayFormula(this, formula);
    if (this.hasOutputTarget) this.outputTarget.classList.remove('hidden');
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.remove('hidden');
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent =
        'Copied to clipboard. Paste under Custom formula is.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showSuccessNudge() {
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Nice. 1 free generation left on this tool.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove('hidden');
    }
    this.hideResult();
  }
}
